import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapIcon from '@mui/icons-material/Map';
import { useJsApiLoader } from '@react-google-maps/api';
import { expandCountryForGeocode } from '../../utils/geoUtils';

/** Explicit px height — % height breaks inside MUI Dialog flex layout. */
const MAP_PICKER_HEIGHT_PX = 420;
const INDIA_FALLBACK = { lat: 20.5937, lng: 78.9629 };

function parseInitialPosition(latStr, lngStr) {
  const la = Number(String(latStr ?? '').trim());
  const lo = Number(String(lngStr ?? '').trim());
  if (
    Number.isFinite(la) &&
    Number.isFinite(lo) &&
    Math.abs(la) <= 90 &&
    Math.abs(lo) <= 180
  ) {
    return { lat: la, lng: lo };
  }
  return { ...INDIA_FALLBACK };
}

function formatCoordString(n) {
  const x = Number(Number(n).toFixed(7));
  return String(x);
}

/**
 * @returns {Promise<{ ok: true, lat: number, lng: number } | { ok: false, status: string }>}
 */
function geocodeAddress(geocoder, address) {
  return new Promise((resolve) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        resolve({ ok: true, lat: loc.lat(), lng: loc.lng() });
      } else {
        resolve({ ok: false, status: String(status || 'UNKNOWN') });
      }
    });
  });
}

const GEOCODING_API_CONSOLE =
  'https://console.cloud.google.com/apis/library/geocoding-backend.googleapis.com';

function isGeocodingApiDisabledStatus(status) {
  return String(status || '') === 'REQUEST_DENIED';
}

/** Build geocode strings: core = hotel name + street + pincode; tail = city + country for disambiguation. */
function buildGeocodeQueries(addressParts) {
  const name = addressParts?.name && String(addressParts.name).trim();
  const addr = addressParts?.address && String(addressParts.address).trim();
  const pin = addressParts?.pincode && String(addressParts.pincode).trim();
  const city = addressParts?.city && String(addressParts.city).trim();
  const country = expandCountryForGeocode(addressParts?.country);
  const tail = [city, country].filter(Boolean).join(', ');
  const core = [name, addr, pin].filter(Boolean).join(', ');
  const out = [];
  if (core && tail) out.push(`${core}, ${tail}`);
  else if (core) out.push(core);
  if (addr && pin && tail && `${addr}, ${pin}, ${tail}` !== out[0]) {
    out.push(`${addr}, ${pin}, ${tail}`);
  }
  if (name && city && tail && `${name}, ${city}, ${tail}` !== out[0]) {
    out.push(`${name}, ${city}, ${tail}`);
  }
  return [...new Set(out)];
}

/**
 * Buttons + dialog to suggest coordinates from address (Geocoder) or pick on a map (draggable marker).
 *
 * @param {(message: string, severity?: 'success'|'error'|'warning'|'info') => void} [onNotify] — e.g. same stack as Dynamic Pricing (`PageToastStack`).
 */
export default function HotelLatLngAssist({
  apiKey,
  addressParts,
  latitude,
  longitude,
  isDark = false,
  onApplyCoords,
  onNotify,
}) {
  // Same id as CompetitorLocationMap so only one Maps script loads (second id can break tiles).
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'nexa-competitor-map-script',
    googleMapsApiKey: apiKey || '',
    version: 'weekly',
  });

  const [mapOpen, setMapOpen] = useState(false);
  const [pickerPos, setPickerPos] = useState(() => parseInitialPosition(latitude, longitude));
  const [mapBoot, setMapBoot] = useState({ center: INDIA_FALLBACK, key: 0 });
  const [suggesting, setSuggesting] = useState(false);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const mapContainerRef = useRef(null);

  const geocodeQueries = useMemo(() => buildGeocodeQueries(addressParts), [addressParts]);
  const canSuggestFromDetails = useMemo(() => {
    const name = addressParts?.name && String(addressParts.name).trim();
    const addr = addressParts?.address && String(addressParts.address).trim();
    const pin = addressParts?.pincode && String(addressParts.pincode).trim();
    return !!(name || addr || pin);
  }, [addressParts]);

  const notify = useCallback(
    (message, severity = 'info') => {
      if (onNotify) onNotify(message, severity);
    },
    [onNotify],
  );

  const handleSuggestFromAddress = useCallback(async () => {
    if (!apiKey) {
      notify('Google Maps is not configured. Add a Maps API key for your site.', 'warning');
      return;
    }
    if (!canSuggestFromDetails) {
      notify('Add property name, street address, or pincode first, then try again.', 'warning');
      return;
    }
    if (loadError) {
      notify('Google Maps failed to load. Check the API key and browser console.', 'error');
      return;
    }
    if (!isLoaded || !window.google?.maps?.Geocoder) {
      notify('Maps is still loading — wait a moment and try again.', 'info');
      return;
    }
    setSuggesting(true);
    const geocoder = new window.google.maps.Geocoder();
    let hit = null;
    let lastStatus = 'UNKNOWN';
    for (let i = 0; i < geocodeQueries.length; i += 1) {
      const q = geocodeQueries[i];
      if (!q) continue;
      const res = await geocodeAddress(geocoder, q);
      if (res.ok) {
        hit = res;
        break;
      }
      lastStatus = res.status;
      if (isGeocodingApiDisabledStatus(res.status)) break;
    }
    setSuggesting(false);
    if (hit) {
      onApplyCoords(formatCoordString(hit.lat), formatCoordString(hit.lng));
      notify('Coordinates filled from hotel name, address, and pincode. Check on the map if needed, then Save.', 'success');
    } else if (isGeocodingApiDisabledStatus(lastStatus)) {
      notify(
        `Geocoding API is not enabled for this Maps key’s Google Cloud project. Open Google Cloud Console and enable “Geocoding API” for the same project: ${GEOCODING_API_CONSOLE} — then try again. You can still set the pin with “Adjust on map”.`,
        'error',
      );
    } else if (lastStatus === 'OVER_QUERY_LIMIT') {
      notify('Geocoding quota exceeded. Try again later or use Adjust on map.', 'warning');
    } else {
      notify(
        'Could not find a match. Refine name, address, or pincode — or use Adjust on map.',
        'warning',
      );
    }
  }, [apiKey, geocodeQueries, canSuggestFromDetails, isLoaded, loadError, notify, onApplyCoords]);

  const handleOpenMap = useCallback(() => {
    if (!apiKey) {
      notify('Google Maps is not configured.', 'warning');
      return;
    }
    if (loadError) {
      notify('Google Maps failed to load.', 'error');
      return;
    }
    if (!isLoaded) {
      notify('Maps is still loading — wait a moment.', 'info');
      return;
    }
    const center = parseInitialPosition(latitude, longitude);
    setPickerPos(center);
    setMapBoot((prev) => ({ center, key: prev.key + 1 }));
    setMapOpen(true);
  }, [apiKey, isLoaded, loadError, latitude, longitude, notify]);

  /** Imperative Map: MUI Dialog applies CSS transforms to Paper; react-google-maps often shows a blank map. Native API + resize avoids that. */
  useEffect(() => {
    if (!mapOpen || !isLoaded || !mapContainerRef.current) return undefined;
    const g = window.google?.maps;
    if (!g) return undefined;

    const el = mapContainerRef.current;
    const center = mapBoot.center;
    const map = new g.Map(el, {
      center,
      zoom: 16,
      streetViewControl: false,
      mapTypeControl: true,
      fullscreenControl: true,
      gestureHandling: 'greedy',
    });
    mapInstanceRef.current = map;

    const marker = new g.Marker({
      position: center,
      map,
      draggable: true,
      optimized: false,
    });
    markerRef.current = marker;

    const clickListener = map.addListener('click', (e) => {
      if (e.latLng) {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        marker.setPosition({ lat, lng });
        setPickerPos({ lat, lng });
      }
    });
    const dragListener = marker.addListener('dragend', (e) => {
      if (e.latLng) {
        setPickerPos({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      }
    });

    const bumpResize = () => g.event.trigger(map, 'resize');
    window.requestAnimationFrame(() => window.requestAnimationFrame(bumpResize));
    const t1 = window.setTimeout(bumpResize, 100);
    const t2 = window.setTimeout(bumpResize, 350);
    const t3 = window.setTimeout(bumpResize, 700);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      g.event.removeListener(clickListener);
      g.event.removeListener(dragListener);
      marker.setMap(null);
      markerRef.current = null;
      mapInstanceRef.current = null;
    };
  }, [mapOpen, isLoaded, mapBoot.key, mapBoot.center]);

  const handleConfirmMap = useCallback(() => {
    onApplyCoords(formatCoordString(pickerPos.lat), formatCoordString(pickerPos.lng));
    setMapOpen(false);
    notify('Coordinates updated from the map. Click Save to persist.', 'success');
  }, [onApplyCoords, notify, pickerPos.lat, pickerPos.lng]);

  const keyMissing = !apiKey;
  const mapsBroken = !!loadError;
  const disabled = keyMissing || mapsBroken || !isLoaded;

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }} useFlexGap flexWrap="wrap">
        <Button
          type="button"
          variant="outlined"
          size="small"
          startIcon={suggesting ? <CircularProgress size={16} color="inherit" /> : <MyLocationIcon />}
          disabled={disabled || suggesting}
          onClick={handleSuggestFromAddress}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#4ce1b6',
            color: '#4ce1b6',
            '&:hover': { borderColor: '#4ce1b6', bgcolor: 'rgba(76, 225, 182, 0.08)' },
          }}
        >
          Suggest from name, address and pincode
        </Button>
        <Button
          type="button"
          variant="outlined"
          size="small"
          startIcon={<MapIcon />}
          disabled={disabled}
          onClick={handleOpenMap}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#4ce1b6',
            color: '#4ce1b6',
            '&:hover': { borderColor: '#4ce1b6', bgcolor: 'rgba(76, 225, 182, 0.08)' },
          }}
        >
          Adjust on map
        </Button>
      </Stack>
      {keyMissing && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: isDark ? '#888' : '#757575' }}>
          Configure <code>GOOGLE_MAPS_API_KEY</code> to enable address lookup and the map picker.
        </Typography>
      )}
      {!keyMissing && mapsBroken && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'error.main' }}>
          Google Maps could not load. Check that Geocoding and Maps JavaScript API are enabled for your key.
        </Typography>
      )}
      {!keyMissing && !mapsBroken && !isLoaded && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="caption" sx={{ color: isDark ? '#888' : '#757575' }}>
            Loading Google Maps…
          </Typography>
        </Box>
      )}
      {!keyMissing && !mapsBroken && isLoaded && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: isDark ? '#888' : '#757575' }}>
          Address lookup uses Google’s <strong>Geocoding API</strong> — enable it on the same Cloud project as your Maps
          key (in addition to Maps JavaScript API).
        </Typography>
      )}

      <Dialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDark ? '#2a2a31' : '#fff',
            backgroundImage: 'none',
            transform: 'none',
          },
        }}
      >
        <DialogTitle sx={{ color: isDark ? '#fff' : '#212121', fontWeight: 700 }}>
          Set property location
        </DialogTitle>
        <DialogContent sx={{ pt: 1, overflow: 'hidden' }}>
          <Typography variant="body2" sx={{ mb: 1.5, color: isDark ? '#bbb' : '#616161' }}>
            Drag the marker or click the map to place it. Zoom for accuracy, then confirm.
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: MAP_PICKER_HEIGHT_PX,
              minHeight: MAP_PICKER_HEIGHT_PX,
              position: 'relative',
              borderRadius: 1,
              overflow: 'hidden',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
            }}
          >
            <Box
              ref={mapContainerRef}
              sx={{
                width: '100%',
                height: MAP_PICKER_HEIGHT_PX,
                minHeight: MAP_PICKER_HEIGHT_PX,
                bgcolor: isDark ? '#1f1f24' : '#e8eaed',
              }}
            />
            {mapOpen && !isLoaded && !loadError && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isDark ? '#1f1f24' : '#f5f5f5',
                  pointerEvents: 'none',
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: isDark ? '#999' : '#757575' }}>
            Lat {formatCoordString(pickerPos.lat)}, Lng {formatCoordString(pickerPos.lng)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMapOpen(false)} sx={{ textTransform: 'none', color: isDark ? '#ccc' : undefined }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmMap}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#4ce1b6',
              color: isDark ? '#0d1f1a' : '#fff',
              '&:hover': { bgcolor: '#3dd4a8' },
            }}
          >
            Use these coordinates
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
